import i18n from '@/lib/i18n'
import type { MemberField, MemberFieldOption, MemberFieldType, MemberFieldValue } from '@/lib/sdk-official/resources/member-import'

/** A field with everything needed to draw and validate it, and the value it holds. */
export interface FieldWithValue {
  key: string
  label: string
  type: MemberFieldType
  options?: MemberFieldOption[] | null
  required: boolean
  /** MULTI_SELECT only: the fewest and most options to mark (null = no bound). */
  minSelections?: number | null
  maxSelections?: number | null
  /** TEXT only: the hint shown under the input (the server checks the pattern). */
  patternMessage?: string | null
  /** As the form holds it: text, and a MULTI_SELECT as a JSON array of option values ('' when none). */
  value: string
}

/**
 * F-149: the form keeps every value as text, so a MULTI_SELECT travels in it
 * as a JSON array of option values, and `''` when nothing is marked (an empty
 * list reads as empty to `missingRequired` and `changedValues`).
 */
export const encodeMulti = (list: string[]): string => (list.length ? JSON.stringify(list) : '')

/** The option values a form value of a MULTI_SELECT holds. */
export const decodeMulti = (value: string | string[] | null | undefined): string[] => {
  if (Array.isArray(value)) return value.map(String)
  const text = String(value ?? '').trim()
  if (!text) return []
  if (text.startsWith('[')) {
    try {
      const list = JSON.parse(text)
      if (Array.isArray(list)) return list.map(String)
    } catch {
      /* not a list: one value below */
    }
  }
  return [text]
}

/** A value from the API as the form holds it. */
const formValue = (type: MemberFieldType, value: string | string[] | null | undefined): string => {
  if (type === 'MULTI_SELECT') return encodeMulti(decodeMulti(value))
  return Array.isArray(value) ? value.join(', ') : value ?? ''
}

/** The labels of the marked options, in the options' order (removed ones never come from the API). */
export const multiLabels = (options: MemberFieldOption[] | null | undefined, value: string | string[] | null | undefined): string[] => {
  const own = new Set(decodeMulti(value))
  return (options ?? []).filter((o) => own.has(String(o.value))).map((o) => o.label)
}

/**
 * The form's values as the API takes them: a MULTI_SELECT as the array of
 * option values (`null` clears it), anything else as typed.
 */
export const apiValues = (
  fields: Pick<FieldWithValue, 'key' | 'type'>[],
  values: Record<string, string>
): Record<string, string | string[] | null> => {
  const typeOf = new Map(fields.map((f) => [f.key, f.type]))
  return Object.fromEntries(Object.entries(values).map(([key, value]) => {
    if (typeOf.get(key) !== 'MULTI_SELECT') return [key, value]
    const list = decodeMulti(value)
    return [key, list.length ? list : null]
  }))
}

/** An http(s) URL as a link target, or null: any other scheme (javascript:, data:) is never linked. */
export const safeHref = (value: string): string | null => {
  try {
    const url = new URL(value.trim())
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null
  } catch {
    return null
  }
}

/**
 * The profile's values on top of the community's definitions.
 *
 * The team reads `/member-fields` (options, required, order); the member
 * reading their own profile cannot, so the profile's own `fields` carry what
 * is needed then. A value whose field was deleted is not shown (F-85 critério 28).
 */
export const mergeFields = (definitions: MemberField[] | null, values: MemberFieldValue[] | undefined | null): FieldWithValue[] => {
  const byKey = new Map((values ?? []).map((v) => [v.key, v]))
  if (definitions && definitions.length) {
    return [...definitions]
      .sort((a, b) => a.order - b.order)
      .map((d) => ({
        key: d.key,
        label: d.label,
        type: d.type,
        options: d.options ?? byKey.get(d.key)?.options ?? null,
        required: !!d.required,
        minSelections: d.minSelections ?? byKey.get(d.key)?.minSelections ?? null,
        maxSelections: d.maxSelections ?? byKey.get(d.key)?.maxSelections ?? null,
        patternMessage: d.patternMessage ?? byKey.get(d.key)?.patternMessage ?? null,
        value: formValue(d.type, byKey.get(d.key)?.value)
      }))
  }
  return (values ?? []).map((v) => ({
    key: v.key,
    label: v.label,
    type: v.type,
    options: v.options ?? null,
    required: !!v.required,
    minSelections: v.minSelections ?? null,
    maxSelections: v.maxSelections ?? null,
    patternMessage: v.patternMessage ?? null,
    value: formValue(v.type, v.value)
  }))
}

export const valuesOf = (fields: FieldWithValue[]): Record<string, string> =>
  Object.fromEntries(fields.map((f) => [f.key, f.value]))

/** Keys whose value differs from the starting point, as the PUT takes them (`''` clears). */
export const changedValues = (initial: Record<string, string>, current: Record<string, string>) =>
  Object.fromEntries(Object.entries(current).filter(([key, value]) => (initial[key] ?? '') !== value))

/** The fewest options a MULTI_SELECT answer needs (`required` counts as 1). */
export const minSelectionsOf = (field: Pick<FieldWithValue, 'required' | 'minSelections'>) =>
  Math.max(field.minSelections ?? 0, field.required ? 1 : 0)

/**
 * The required fields left empty, with the message for each, and (F-149) the
 * multiple-choice answers outside their minimum and maximum. An optional
 * multiple choice left blank is fine; once something is marked, the bounds apply.
 */
export const missingRequired = (fields: FieldWithValue[], values: Record<string, string>, message: (label: string) => string) =>
  Object.fromEntries(
    fields.flatMap((f): [string, string][] => {
      const value = values[f.key] ?? ''
      if (f.required && !value.trim()) {
        return [[f.key, f.type === 'MULTI_SELECT' ? i18n.t('members:fieldsSection.tooFew', { label: f.label, count: minSelectionsOf(f) }) : message(f.label)]]
      }
      if (f.type !== 'MULTI_SELECT' || !value.trim()) return []
      const count = decodeMulti(value).length
      const min = minSelectionsOf(f)
      if (count < min) return [[f.key, i18n.t('members:fieldsSection.tooFew', { label: f.label, count: min })]]
      if (f.maxSelections && count > f.maxSelections) return [[f.key, i18n.t('members:fieldsSection.tooMany', { label: f.label, count: f.maxSelections })]]
      return []
    })
  )
